
import { CacheManager, Utils, LzEncryptor } from "../utils";
import { MessageSchemesIcons, NoseurObject, Scheme, Subscriber, Toaster, TypeChecker } from "@rivtn/noseur";
import ffs, { Ffs, Interceptor, RequestProcessor, RequestType, Utils as KyofuucUtils, ResponseProcessor, HttpConfig, KyofuucObject, IndexDbCacheManager } from "kyofuuc";

export interface ApiResponseData<T> {
    data?: T;
    content?: T[];
    size?: number;
    transformed?: any;
    total_pages?: number;
    total_elements?: number;
    number_of_elements?: number;
    pageable?: {
        paged: boolean;
        offset: number;
        page_size: number;
        page_number: number;
    };
};

export interface ApiResponse<T> {
    message: string;
    success: boolean;
    data?: T & ApiResponseData<T>;
    secondary_data: NoseurObject<any>;
};

export type SanitizedResponse<T> = {
    sanitized: T;
    content: T[];
    status: number;
    errors?: any[];
    errorMessage: string;
    apiResponse: ApiResponse<T>;
}

export interface ISecondaryData {
    unreads: NoseurObject<number>;
};

export enum Visibility {

    PUBLIC = "PUBLIC",
    PRIVATE = "PRIVATE",
    TOURNAMENTS = "TOURNAMENTS",
    REQUIRE_SIGN_IN = "REQUIRE_SIGN_IN",

}

export enum Status {

    ACTIVE = "ACTIVE",

}

export const TeamTypesValue: NoseurObject<number> = {

    ALL: -1,
    SOLO: 1,
    DUO: 2,
    TRIO: 3,
    SQUAD: 4,
    SEXTET: 6,

}

export interface IWebsitePage {
    label: string;
    visible?: boolean;
}

export interface IWebsite {

    sponsors: string[];
    visibility: Visibility;
    owns_copyright: boolean;
    social_accounts: NoseurObject<string>;
    design_settings: {
        design_id: string;
        scheme_settings?: {
            colors?: NoseurObject<string>;
        } & NoseurObject<string>;
    };

}

export interface IWebsiteEvent {
    title: string;
    details: string;
    location: string;
    end_date?: string;
    start_date?: string;
    external_id: string;
    action_label: string;
    media_location: string;
    action_location: string;
}

export class BaseService {

    // remove /json route after add formData submission on kyofuuc, and remove the headers this.config
    transport: Ffs;
    interceptor = new Interceptor();
    baseUrl: string = process.env.REACT_APP_OMNEHOST_USER_SKIES_BASE_API_URL ?? "http://127.0.0.1:9310";
    cacheManager = IndexDbCacheManager.getInstance({ encryptKey: true, encryptor: LzEncryptor, bucket: "API_CACHE", storeName: "OMNEHOST_LANDINGS_REQUESTS_CACHE" });

    constructor(baseUrl?: string) {
        this.transport = ffs.init({
            promiscuous: true,
            interceptor: this.interceptor,
            cacheManager: this.cacheManager,
            baseUrl: baseUrl ?? this.baseUrl,
            cacheLifetime: 1000 * 60 * 60 * 24, // 24 hours
            querySerializer: (query: KyofuucObject<any>) => {
                let serializedQuery: string = "";
                let queryParts: string[] = [];
                KyofuucUtils.forEach(query, function serialize(key: string, value: any) {
                    if (value === null || typeof value === 'undefined') {
                        return;
                    }
                    if (!KyofuucUtils.isArray(value)) {
                        value = [value];
                    }

                    KyofuucUtils.forEach(value, function parseValue(_: string, val: any) {
                        if (KyofuucUtils.isDate(val)) {
                            val = val.toISOString();
                        } else if (KyofuucUtils.isObject(val)) {
                            val = KyofuucUtils.safeStringify(val);
                        }
                        queryParts.push(`${KyofuucUtils.encodeParamURI(key)}=${KyofuucUtils.encodeParamURI(val)}`);
                    });
                    serializedQuery = queryParts.join('&');
                });
                return serializedQuery;
            }
        });
        this.interceptor.registerPreRequest((config?: HttpConfig) => {
            const accessToken = CacheManager.get(CacheManager.ACCESS_TOKEN_KEY);
            config!.headers = {
                ...config?.headers,
                Authorization: `Bearer ${accessToken}`,
                HandShake: CacheManager.get(CacheManager.HANDSHAKE_KEY),
            };
        });
        this.interceptor.registerPostResponse((_: any, __: any, response: any) => {
            let { apiResponse }: SanitizedResponse<ISecondaryData> = this.buildResponse(response);
            if (!response.__cached__ && apiResponse && !!apiResponse?.secondary_data) {
                Subscriber.report(Subscriber.KEYS.SECONDARY_DATA, apiResponse?.secondary_data);
            }
        });
    }

    normalizeParams(params: NoseurObject<any>) {
        if (!params.page) params.page = 1;
        if (!params.size) params.size = 20;
        if (!params.sort && params.sort !== null) params.sort = "id,DESC";
        Object.keys(params).forEach((key: string) => {
            const value = params[key];
            if (!TypeChecker.isArray(value)) return;
            /*params[key] = value.reduce((acc: string, v: string, index: number) => {
                if (index > 0) return `${acc}\n${key}=${v}`;
                return `${v}`;
            }, "");*/
        });
        return params;
    }

    private handleResolve<T>(response: T, resolve: Function, postResolve?: Function) {
        let transformedResponse = this.buildResponse(response);
        postResolve?.(transformedResponse);
        resolve(transformedResponse);
    }

    private handleReject(transformedError: any, reject: Function, postReject?: Function) {
        postReject?.(transformedError);
        reject(transformedError);
    }

    report<T>(promise: Promise<any>, postResolve?: Function, postReject?: Function): Promise<SanitizedResponse<any>> {
        return new Promise((resolve, reject) => {
            promise.then((response: T) => {
                this.handleResolve(response, resolve, postResolve);
            }).catch((error: any) => {
                let transformedError = this.buildError(error);
                if (error.response?.status === 401) {
                    this.report(this.transport.post((process.env.REACT_APP_LANUARIUS_BASE_API_URL ?? "http://127.0.0.1:9001") + "/janus/lanuarius/api/v1/user/auth/refresh", {
                        authenticating_platform: "omnehost",
                        access_token: CacheManager.get(CacheManager.ACCESS_TOKEN_KEY),
                        session_token: CacheManager.get(CacheManager.SESSION_TOKEN_KEY),
                    }), ({ sanitized }: SanitizedResponse<any>) => {
                        CacheManager.put(CacheManager.ACCESS_TOKEN_KEY, sanitized.access_token);
                        promise.then((response: T) => {
                            this.handleResolve(response, resolve, postResolve);
                        }).catch(async (error: any) => {
                            this.handleReject(this.buildError(error), reject, postReject);
                        });
                    }).catch(() => {
                        Utils.signIn();
                    });
                    return;
                }
                this.handleReject(transformedError, reject, postReject);
            });
        });
    }

    buildResponse<T>(response: any): SanitizedResponse<T> {
        if (!response.data) return response;
        response.apiResponse = response.data;
        response.sanitized = response.apiResponse?.data;
        response.content = response.apiResponse?.data?.content;
        return response;
    }

    buildError<T>(error: any): SanitizedResponse<T> {
        if (!error) error = {};
        error.message = error?.response?.data?.message ?? error?.response?.message ?? error?.data?.message ?? error?.message ?? "An error occur please try again later";
        error.errors = error?.response?.data?.errors;
        error.errorMessage = error.message;
        error.status = error.response?.status ?? -1;
        return error;
    }

    mockResolve(timeout: number, response: any, postResolve?: Function, postReject?: Function): Promise<any> {
        return this.report(new Promise((resolve, _) => {
            setTimeout(() => resolve(response), timeout);
        }), postResolve, postReject);
    }

    mockReject(timeout: number, error: any, postResolve?: Function, postReject?: Function): Promise<any> {
        return this.report(new Promise((_, reject) => {
            setTimeout(() => reject(error), timeout);
        }), postResolve, postReject);
    }

    performHandShake() {
        return this.mockResolve(100, {
            data: {
                succuss: true,
                message: "Success",
                data: {
                    access_code: "kjkjkdjkjdksjkdjskdjk"
                }
            }
        }, ({ sanitized }: SanitizedResponse<any>) => {
            CacheManager.put(CacheManager.HANDSHAKE_KEY, sanitized.code);
        });
        //return this.report(this.transport.get("/v1/handshake")); // validates user agent and ip on each request
    }

    static reportError<T>(response: SanitizedResponse<T>) {
        Toaster.toast({
            lifetime: 5000,
            foreScheme: true,
            scheme: Scheme.DANGER,
            showProgressbar: true,
            pauseDelayOnHover: true,
            style: { background: "white" },
            icon: MessageSchemesIcons.INFO,
            content: (response.errors?.length ? response.errors.join("\n") : response?.errorMessage ?? `${response}`),
        });
    }

}

RequestProcessor.register(RequestType.JSON, (data: object) => {
    return {
        contentType: "application/json",
        buffer: KyofuucUtils.safeStringify(data),
    } as any;
});

// todo handle responseType: "noContent"
ResponseProcessor.register("NOCONTENT", (_: any) => {
    return {
        contentType: "noContent",
        buffer: "",
    } as any;
});
